/**
 * Normalizes comp id arrays from strings or objects (Zillow / agent / API shapes).
 *
 * @param {unknown} items
 * @returns {string[]}
 */
export function normalizeCompIds(items) {
  if (!Array.isArray(items)) return [];
  const ids = items
    .map((item) => {
      if (typeof item === "string") return item;
      if (!item || typeof item !== "object") return "";
      return item.id || item.comp_id || item.compId || item.property_id || item.propertyId || "";
    })
    .map((id) => (typeof id === "string" ? id.trim() : ""))
    .filter(Boolean);
  return ids.filter((id, index) => ids.indexOf(id) === index);
}

/**
 * Collects comp IDs from both `comp_ids` and `comps` on additional_attributes so Zillow-only
 * rows stored under `comps` are not dropped when merging with AI-only `comp_ids`.
 *
 * @param {object|null|undefined} additionalAttributes
 * @returns {string[]}
 */
export function getAllCompIdsFromAdditionalAttributes(additionalAttributes) {
  if (!additionalAttributes || typeof additionalAttributes !== "object") return [];
  const fromIds = normalizeCompIds(additionalAttributes.comp_ids);
  const fromComps = normalizeCompIds(additionalAttributes.comps);
  return [...new Set([...fromIds, ...fromComps])];
}

/**
 * Unions IDs already associated with the property, optional live calculator IDs, and incoming
 * IDs (e.g. comps agent or QMA) so new AI comps augment Zillow comps instead of replacing them.
 *
 * @param {object|null|undefined} additionalAttributes - typically property.additional_attributes
 * @param {unknown} incomingCompIds
 * @param {unknown} [liveCalculatorCompIds] - optional `onCompIdsChange` / compsStore snapshot
 * @returns {string[]}
 */
export function mergeCompIdsForPropertyPatch(additionalAttributes, incomingCompIds, liveCalculatorCompIds) {
  const fromProp = getAllCompIdsFromAdditionalAttributes(additionalAttributes);
  const fromIncoming = normalizeCompIds(Array.isArray(incomingCompIds) ? incomingCompIds : []);
  const fromLive =
    Array.isArray(liveCalculatorCompIds) && liveCalculatorCompIds.length > 0 ? normalizeCompIds(liveCalculatorCompIds) : [];
  return [...new Set([...fromProp, ...fromLive, ...fromIncoming])];
}

/**
 * Strips comp# or property# prefix for comparison / API variants.
 *
 * @param {unknown} id
 * @returns {string}
 */
export function stripCompIdPrefix(id) {
  if (typeof id !== "string") return "";
  return id.replace(/^(comp#|property#)/i, "").trim();
}

/**
 * Strips a leading `property#` prefix (anchored) and trims. Used to normalize property/comp ids
 * to their bare suffix form for currentCompIds and getPropComps variables.
 *
 * @param {unknown} id
 * @returns {string}
 */
export function stripPropertyPrefix(id) {
  if (typeof id !== "string") return "";
  return id.replace(/^property#/, "").trim();
}

/**
 * Compares two comp/property id strings allowing comp#/property# prefix differences.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function compIdsReferToSameEntity(a, b) {
  const sa = stripCompIdPrefix(a);
  const sb = stripCompIdPrefix(b);
  return sa.length > 0 && sa === sb;
}

/**
 * Expands canonical comp IDs for updateProperty delete_comp_ids so the resolver can match
 * both comp#-prefixed and bare suffix forms that may exist on the property record.
 *
 * @param {string[]} canonicalIds
 * @returns {string[]}
 */
export function expandDeleteCompIdsForPropertyMutation(canonicalIds) {
  if (!Array.isArray(canonicalIds)) return [];
  const set = new Set();
  for (const raw of canonicalIds) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const trimmed = raw.trim();
    set.add(trimmed);
    const bare = stripCompIdPrefix(trimmed);
    if (bare && bare !== trimmed) set.add(bare);
    if (bare && !/^comp#/i.test(trimmed)) set.add(`comp#${bare}`);
  }
  return [...set];
}

/**
 * Merges property.additional_attributes so comp_ids and comps match remaining comps after deletion.
 * Zillow / link flows often store IDs under `comps` as a string[]; AI flows may use comp_ids and/or object comps.
 *
 * @param {object|null|undefined} property
 * @param {string[]} remainingCompIds
 * @returns {Record<string, unknown>}
 */
export function buildAdditionalAttributesAfterCompDeletion(property, remainingCompIds) {
  const remaining = normalizeCompIds(remainingCompIds);
  let existing = {};
  try {
    const raw = property?.additional_attributes;
    if (raw != null && typeof raw === "string") {
      existing = JSON.parse(raw || "{}");
    } else if (raw && typeof raw === "object") {
      existing = { ...raw };
    }
  } catch {
    existing = {};
  }
  if (!existing || typeof existing !== "object") {
    existing = {};
  }

  const next = { ...existing, comp_ids: remaining };

  if (Array.isArray(existing.comps)) {
    const comps = existing.comps;
    if (comps.length === 0) {
      next.comps = [];
    } else if (comps.every((c) => typeof c === "string")) {
      next.comps = comps.filter(
        (cid) => typeof cid === "string" && remaining.some((rid) => compIdsReferToSameEntity(rid, cid)),
      );
    } else {
      next.comps = comps.filter(
        (c) =>
          c &&
          typeof c === "object" &&
          typeof c.id === "string" &&
          remaining.some((rid) => compIdsReferToSameEntity(rid, c.id)),
      );
    }
  }

  return next;
}

/**
 * Replaces comp_ids and comps on additional_attributes with a new agent comp set (regen — no merge).
 *
 * @param {object|null|undefined} property
 * @param {string[]} newCompIds
 * @returns {Record<string, unknown>}
 */
export function buildAdditionalAttributesForCompReplace(property, newCompIds) {
  const normalized = normalizeCompIds(newCompIds);
  let existing = {};
  try {
    const raw = property?.additional_attributes;
    if (raw != null && typeof raw === "string") {
      existing = JSON.parse(raw || "{}");
    } else if (raw && typeof raw === "object") {
      existing = { ...raw };
    }
  } catch {
    existing = {};
  }
  if (!existing || typeof existing !== "object") {
    existing = {};
  }

  const next = { ...existing, comp_ids: normalized };

  if (Array.isArray(existing.comps)) {
    const comps = existing.comps;
    if (comps.length === 0 || normalized.length === 0) {
      next.comps = normalized.length === 0 ? [] : [...normalized];
    } else if (comps.every((c) => typeof c === "string")) {
      next.comps = normalized.length > 0 ? [...normalized] : [];
    } else {
      next.comps = comps.filter(
        (c) =>
          c &&
          typeof c === "object" &&
          typeof c.id === "string" &&
          normalized.some((rid) => compIdsReferToSameEntity(rid, c.id)),
      );
      if (next.comps.length === 0 && normalized.length > 0) {
        next.comps = [...normalized];
      }
    }
  } else if (normalized.length > 0) {
    next.comps = [...normalized];
  } else {
    next.comps = [];
  }

  return next;
}
